import type { Draft, TemplateRow } from './types';

export const TOKEN_RE = /{{\s*([^{}]+?)\s*}}/g;

/** Mirrors worker/lib/emailTemplates.ts's templateWarnings() exactly, run
    client-side so the warning badge and inline warning update live as Henry
    types, without waiting on a preview round-trip. */
export function unrecognisedPlaceholders(placeholders: readonly string[], ...sources: string[]): string[] {
  const supported = new Set(placeholders);
  const unknown = new Set<string>();
  for (const source of sources) {
    for (const match of source.matchAll(TOKEN_RE)) {
      const name = match[1].trim();
      if (!supported.has(name)) unknown.add(name);
    }
  }
  return [...unknown].map((name) => `{{${name}}}`);
}

/** Short horizontal-tab label for a template row. The invoice ladder's five
    stages get the same T-3/Due/+7d/+14d/+21d codes stageLabel() above puts
    on the automation card's ladder chips; every other template's label is
    "Stage N · <short name>" or a bare name, so falling back to whatever
    follows "· " (or the whole label, for the two-stage testimonial ask)
    keeps every tab short without a second lookup table. */
export function templateTabLabel(t: TemplateRow): string {
  const ladderMatch = t.template_key.match(/^invoice_reminder_(t_minus_3|due|plus_(\d+))$/);
  if (ladderMatch) {
    if (ladderMatch[1] === 't_minus_3') return 'T-3';
    if (ladderMatch[1] === 'due') return 'Due';
    return `+${ladderMatch[2]}d`;
  }
  const parts = t.label.split('·');
  return parts.length > 1 ? parts[1].trim() : t.label;
}

/** Renders the sandboxed preview's actual HTML down to plain text for the
    Plain-text toggle: parsed into a detached HTML document (never touches
    the live DOM, mirroring the iframe's own sandbox="" isolation) so
    paragraph/line breaks survive as real newlines instead of the run-on
    string textContent on the raw HTML would produce. */
export function htmlToPlainText(html: string): string {
  const doc = document.implementation.createHTMLDocument('preview');
  doc.body.innerHTML = html;
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.body.querySelectorAll('p, div, li').forEach((el) => el.insertAdjacentText('afterend', '\n\n'));
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

// businessInfo.ts's documented default identity — the client has no
// endpoint exposing the live env-configured BUSINESS_NAME/
// BUSINESS_CONTACT_EMAIL, so the preview's "From" line shows this known
// default rather than a fetched value. Sample-data labelling only; the
// worker's own senders always resolve the real env value at send time.
export const SAMPLE_FROM = 'Henry Sheahan Productions <henry@henrysheahanproductions.com>';

export function savedDraft(t: TemplateRow): Draft {
  return { subject: t.subject_override ?? t.subject_default, body: t.body_override ?? t.body_default };
}

export function draftDirty(t: TemplateRow, draft: Draft | undefined): boolean {
  if (!draft) return false;
  const saved = savedDraft(t);
  return draft.subject !== saved.subject || draft.body !== saved.body;
}
